// eslint-disable-next-line import/no-unresolved
import 'normalize.css'; // 重置样式
import scrollbar from './scrollbar';
import {
  EventPublisher,
  consoleBanner,
  addClassName,
  removeClassName,
  debounce,
} from '../../modules/utils';
import { asyncLoadPage } from '../../index';

// 页面转场动画类型定义
export const PAGE_ANI_DEFAULT = ''; // 缺省动画类型（一般是由浏览器前进后退触发的路由跳转）
export const PAGE_ANI_AUTO = '+'; // 自动判断动画类型
export const PAGE_ANI_NONE = '-'; // 无动画类型
export const PAGE_ANI_NEXT = '>'; // 前进动画类型
export const PAGE_ANI_BACK = '<'; // 后退动画类型

export default {
  name: 'appbase',
  props: {
    /**
     * 是否开启转场动画
     */
    enableAni: {
      type: Boolean,
      default: true,
    },
    beforePageTo: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      pageTransition: {
        class: '', // 页面动画class
        ani: PAGE_ANI_AUTO, // 页面转场动画方向
        from: null, // 离开的页面路由
        to: null, // 进入的页面路由
      },
      pageTransferData: null, // 页面跳转时传送的数据
      appAlive: true,
      appClass: [], // app级别样式类（页面切换会自动抹除）
      appData: {}, // app级别的存储(代替vuex)
      appEvent: new EventPublisher(), // app级事件订阅-发布器
      keepAlives: [],
      leaveDelay: 300, // 外跳离开时延迟的时间（1、给统计发送留时间；2、给离场动画留时间）
    };
  },
  watch: {
    // 页面路由跳转时（切换前）
    $route(to, from) {
      this.$OnRoute(to, from);
    },
  },
  computed: {
    $page: {
      cache: false,
      get() {
        return this.$refs.page;
      },
    },
  },
  created() {
    // 版本打印
    this.$LogAppInfo();
    // 设置$app对象为全局可用
    this.setVueGlobalProperty('$app', this);
    if (this.$config.site.visualScrollbar) this.setVisualScrollbar(true);
    // 初始化页面缓存
    this.$GetKeepAlives();
  },
  mounted() {
    // 禁用指定样式元素的触摸操作
    document.body.addEventListener('touchmove', this.$NoTouchMove, { passive: false });

    // 执行回调，通知HobbitApp已经初始化好
    this.$ctx.$AppReadyResolve();
  },
  destroyed() {
    this.setVisualScrollbar(false);
    document.body.removeEventListener('touchmove', this.$NoTouchMove, { passive: false });
  },
  methods: {
    setPageScroll: debounce((enabled) => {
      if (enabled) {
        removeClassName(document.body, 'noscroll');
      } else {
        addClassName(document.body, 'noscroll');
      }
    }, 100),
    setVisualScrollbar(enabled) {
      if (enabled) scrollbar.init();
      else scrollbar.destroyed();
    },
    addAppClass(className) {
      if (!this.appClass.includes(className)) {
        this.appClass.push(className);
      }
    },
    removeAppClass(className) {
      this.appClass.remove(className);
    },
    getAppData(key) {
      return this.appData[key];
    },
    setAppData(key, value) {
      this.$set(this.appData, key, value);
    },
    removeAppData(key) {
      this.$delete(this.appData, key);
    },
    onEvent(name, handler) {
      this.appEvent.on(name, handler);
    },
    offEvent(name, handler) {
      this.appEvent.off(name, handler);
    },
    emitEvent(name, ...handlerArgs) {
      this.appEvent.emit(name, handlerArgs);
    },
    /**
     * SPA导航至新页面
     * @param {string|object} to 新页面路径（支持相对和绝对路径）或路由导航对象
     * @param {string} ani 转场动画类型
     * @param {boolean} replace 是否替换当前路由历史
     * @param {Object} open 新开窗口参数（包含：name和specs参数）
     * @param {Any} open 传递给下个页面的数据
     */
    pageTo(to, ani = PAGE_ANI_AUTO, replace = false, open = null, transferData) {
      // 是否跨站跳转
      let isCross = null;
      // 修正为相对路径
      to = to.replace(new RegExp(`^${window.location.origin}`, 'i'), '');

      // 跳转动作自定义
      if (this.beforePageTo) {
        const result = this.beforePageTo(to, ani, replace, open, transferData, isCross);
        if (!result) return Promise.resolve();
        to = result.to ?? to;
        ani = result.ani ?? ani;
        replace = result.replace ?? replace;
        open = result.open ?? open;
        transferData = result.transferData ?? transferData;
        isCross = result.isCross ?? isCross;
      }

      // 新窗口跳转，直接打开新窗口
      if (open) {
        return Promise.resolve(window.open(to, open.name, open.specs, replace));
      }

      if (isCross === null && typeof to === 'string') {
        isCross = this.isCrossAppRoute(to);
      }

      if (isCross) {
        // 跨站硬跳转
        if (this.enableAni && ani !== PAGE_ANI_NONE) this.addAppClass('app-leave-active');
        // 延迟200ms才真正跳转，给统计发送和动画留出时间
        return new Promise(resolve => {
          window.setTimeout(() => {
            if (ani !== PAGE_ANI_NONE) {
              // 兜底事件，确保恢复页面可见，避免后退回来页面不可见
              let fallbackTime = new Date().getTime();
              const fallbackInterval = window.setInterval(() => {
                // 启动轮训检查器，每250毫秒检查一次，如果执行时间点间隔过大（>500ms），则可以认定为返回到该页面
                const nextFallbackTime = new Date().getTime();
                if (nextFallbackTime - fallbackTime > 500) {
                  this.removeAppClass('app-leave-active');
                  window.clearTimeout(fallbackInterval);
                } else {
                  fallbackTime = nextFallbackTime;
                }
              }, 250);
              // 后退回来页面可见
              window.addEventListener('pageshow', () => {
                window.clearTimeout(fallbackInterval);
                this.removeAppClass('app-leave-active');
              }, { once: true });
            }
            window.location[replace ? 'replace' : 'assign'](to);
            resolve();
          }, this.leaveDelay);
        });
      }

      // 非跨站软跳转
      this.pageTransition.ani = ani;
      this.pageTransferData = transferData;
      return this.$router[replace ? 'replace' : 'push'](to).catch(error => {
        switch (error?.type) {
          case 4: // aborted
          case 8: // cancelled
          case 16: // duplicated
            console.warn(error.message);
            return error?.type;
          default: throw error;
        }
      });
    },
    /**
     * 导航至新页面且传送数据
     */
    pageTransfer(to, data, replace = false, ani = PAGE_ANI_AUTO) {
      return this.pageTo(to, ani, replace, null, data);
    },
    /**
     * SPA导航后退
     */
    pageBack(ani = PAGE_ANI_BACK) {
      this.pageTransition.ani = ani;
      return this.$router.go(-1);
    },
    /**
     * SPA导航替换当前页至新页面（当前页历史中删除）
     */
    pageReplace(to, ani = PAGE_ANI_NONE) {
      return this.pageTo(to, ani, true);
    },
    /**
     * 新开窗口到指定页面
     */
    pageOpen(to, name, specs, replace) {
      return this.pageTo(to, PAGE_ANI_NONE, false, { name, specs, replace });
    },
    /**
     * 检查一个路由值是否是外跳链接（硬跳转，而不是SPA内部跳转）
     */
    isCrossAppRoute(to) {
      if (typeof to === 'string' && /^http[s]?:\/\//i.test(to)) return true;
      const matched = this.$router.resolve(to).resolved?.matched;
      return !(matched && matched.length && matched[0].path !== '*');
    },
    /**
     * 刷新页面
     * @param {Boolean} hard 是否硬刷新
     */
    pageReload(hard = false) {
      if (hard) {
        window.location.reload();
      } else {
        this.pageTransition.ani = PAGE_ANI_NONE;
        this.appAlive = false;
        return this.$nextTick(() => {
          this.$OnRoute(this.$route, this.$route);
          this.appAlive = true;
        });
      }
      return Promise.resolve();
    },
    /**
     * 页面预加载
     * @param {String} page 要预加载的页面id
     * @param {Object} args 预加载参数
     */
    pagePreload(page, args) {
      return asyncLoadPage(
        page,
        true,
        args,
        this,
      );
    },
    /**
     * 根据名称获取组件集合
     * @param {String} name 组件名
     * @param {VM} vm 限定的VM，如不传则默认为$app
     */
    findComponents(name, vm, result) {
      if (!result) result = [];
      if (!vm) vm = this;
      if (vm.$children.length) {
        for (const cvm of vm.$children) {
          this.findComponents(name, cvm, result);
        }
      }
      if (vm.$vnode.tag.indexOf(name) !== -1) {
        result.push(vm);
      }
      return result;
    },
    $OnRoute(to, from) {
      // 抹除所有App级别样式类，避免跨页污染
      this.appClass.clear();

      // 更新页面转场基本数据
      this.pageTransition.to = to;
      if (from?.meta) {
        from.meta.position = {
          x: document.documentElement.scrollLeft || document.body.scrollLeft,
          y: document.documentElement.scrollTop || document.body.scrollTop,
        };
        this.pageTransition.from = from;
      }

      // 计算并更新页面转场动画样式类
      this.$SwitchTransitionClass(to, from);

      // 更新降级语言标志
      this.$ctx.revertLang = to.meta.revertLangOption?.[this.$ctx.lang];

      // 路由切换事件
      this.$emit('route', to, from);

      // 更新Body样式类
      this.$SetBodyClass();
    },
    $BeforePageEnter(el) {
      // 恢复keepAlive页面的滚动条（动画中）
      const keepAlive = this.pageTransition.to?.meta?.keepAlive;
      if (keepAlive) {
        const position = this.pageTransition.to?.meta?.position;
        if (position) {
          this.$nextTick(() => {
            el.scrollTo(position.x, position.y);
          });
        }
      }
    },
    $AfterPageEnter() {
      // 恢复keepAlive页面的滚动条（动画后）
      const keepAlive = this.pageTransition.to?.meta?.keepAlive;
      if (keepAlive) {
        const position = this.pageTransition.to?.meta?.position;
        if (position) {
          window.scrollTo(position.x, position.y);
        }
      } else {
        window.scrollTo(0, 0);
      }
    },
    /**
     * 转场动画逻辑
     * @param {route} to 前页路由
     * @param {route} from 后页路由
     */
    $SwitchTransitionClass(to, from) {
      if (!this.enableAni) return;
      /* 因安卓大部分手机也开始支持侧滑后退了，因此浏览器前进后对全都禁用掉自动动画
      if (this.pageTransition.ani === PAGE_ANI_DEFAULT
        && this.$ctx.system !== this.$ctx.SYSTEM.IOS) {
        // iOS因为有系统自带侧滑动画，因此关闭自身动画
        // 非iOS视为自动判断动画类型
        this.pageTransition.ani = PAGE_ANI_AUTO;
      }
      */

      // 自动识别动画类型
      if (this.pageTransition.ani === PAGE_ANI_AUTO) {
        if (!from.meta.index || !to.meta.index) {
          this.pageTransition.ani = PAGE_ANI_NONE;
        } else if (to.meta.index >= from.meta.index) {
          this.pageTransition.ani = PAGE_ANI_NEXT;
        } else {
          this.pageTransition.ani = PAGE_ANI_BACK;
        }
      }

      // 按指定的动画方式进行动画
      switch (this.pageTransition.ani) {
        default:
        case PAGE_ANI_NONE:
          // 无动画
          this.pageTransition.class = '';
          break;
        case PAGE_ANI_NEXT:
          // 向左划出页面动画（前进）
          this.pageTransition.class = 'page-next';
          break;
        case PAGE_ANI_BACK:
          // 向右推出页面（后退）
          this.pageTransition.class = 'page-back';
          break;
      }

      this.pageTransition.ani = PAGE_ANI_DEFAULT;
    },
    /**
     * 获取持久化页面
     */
    $GetKeepAlives() {
      this.$router.options.routes.forEach(item => {
        if (item.meta.keepAlive) {
          this.keepAlives.push(item.name);
        }
      });
    },
    $SetBodyClass() {
      const renderLang = this.$ctx.revertLang ?? this.$ctx.lang;
      const dir = this.$config.site.bizs?.[this.$ctx.biz]?.langs?.[renderLang]?.dir;

      document.documentElement.setAttribute('lang', renderLang);
      document.documentElement.setAttribute('dir', dir ?? 'ltr');

      const bodyClassNames = [
        `view-${this.$ctx.view}`,
        `system-${this.$ctx.system}`,
        `browser-${this.$ctx.browser}`,
        `client-${this.$ctx.client}`,
        `lang-${renderLang}`,
        `dir-${dir ?? 'ltr'}`,
        `biz-${this.$ctx.biz}`,
        `channel-${this.$ctx.channel}`,
        '-ready-',
      ];
      // eslint-disable-next-line max-len
      const className = bodyClassNames.join(' ').toLowerCase();
      if (document.body.className !== className) {
        document.body.className = className;
      }
    },
    $LogAppInfo() {
      // 版本打印
      consoleBanner('Hobbit App', {
        product: `${this.$config.log.appName}`,
        index: window.location.href,
        hpinfo: `${this.$ctx.publicPath}hp-info.json?${this.$ctx.version}`,
        runtime: this.$ctx.runtime,
        version: this.$ctx.version,
        system: this.$ctx.system,
        client: this.$ctx.client,
        biz: this.$ctx.biz,
        lang: this.$ctx.lang,
        timezone: this.$ctx.timezone,
        channel: `${this.$ctx.channel}/${this.$ctx.channelLevel}`,
        referral: this.$ctx.referralCode,
        view: `${this.$ctx.view}(DPR=${window.devicePixelRatio ?? 1})`,
      });
    },
    $NoTouchMove(e) {
      let el = e.target;
      while (el) {
        if (el.className && el.className?.indexOf('no-touch-move') !== -1) e.preventDefault();
        el = el.parentNode;
      }
    },
  },
};
